import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiCoffee, FiTrash2, FiTrendingUp, FiAlertCircle } = FiIcons;

const FeedingLog = () => {
  const { data, addFeedingLog, deleteEntry } = useData();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    totalFeed: '',
    feedingFrequency: '2x',
    appetiteNotes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addFeedingLog({
      ...formData,
      totalFeed: parseFloat(formData.totalFeed)
    });
    setFormData({
      totalFeed: '',
      feedingFrequency: '2x',
      appetiteNotes: ''
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getTotalFeedLast7Days = () => {
    const last7Days = data.feedingLogs.slice(-7);
    return last7Days.reduce((sum, log) => sum + log.totalFeed, 0);
  };

  const getAverageDailyFeed = () => {
    if (data.feedingLogs.length === 0) return 0;
    const totalFeed = data.feedingLogs.reduce((sum, log) => sum + log.totalFeed, 0);
    return totalFeed / data.feedingLogs.length;
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feeding Log</h1>
          <p className="text-gray-600 mt-1">Track daily feeding records</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Feed</span>
        </button>
      </div>
      
      {/* Educational Tip */}
      <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-green-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">Feeding Tip</h3>
            <div className="mt-1 text-sm text-green-700">
              Feed tilapia 2-3% of their body weight daily. For example, 1000 fish averaging 200g each would need 
              4-6kg of feed per day. Distribute this across 2-3 feedings for better feed conversion and growth rates.
            </div>
          </div>
        </div>
      </div>

      {/* Feed Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Last 7 Days</p>
              <p className="text-2xl font-bold text-gray-900">
                {getTotalFeedLast7Days().toFixed(0)}g
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCoffee} className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-2">Total feed used in the past week</p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Daily Average</p>
              <p className="text-2xl font-bold text-gray-900">
                {getAverageDailyFeed().toFixed(0)}g
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-2">Average amount per day</p>
        </motion.div>
      </div>
      
      {/* Feeding Guide */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Feeding Guide</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="font-medium text-gray-700">Fish Size</div>
            <div className="font-medium text-gray-700">Feed Rate (% body weight)</div>
            <div className="font-medium text-gray-700">Frequency</div>
            
            <div className="text-gray-600">Fry (0.2-1g)</div>
            <div className="text-gray-600">10-15%</div>
            <div className="text-gray-600">4-6x daily</div>
            
            <div className="text-gray-600">Fingerlings (1-20g)</div>
            <div className="text-gray-600">5-8%</div>
            <div className="text-gray-600">3-4x daily</div>
            
            <div className="text-gray-600">Juveniles (20-100g)</div>
            <div className="text-gray-600">3-5%</div>
            <div className="text-gray-600">2-3x daily</div>
            
            <div className="text-gray-600">Adults (&gt;100g)</div>
            <div className="text-gray-600">2-3%</div>
            <div className="text-gray-600">2x daily</div>
          </div>
        </div>
      </div>

      {/* Add Feeding Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Record Feeding</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Feed Given (grams)
              </label>
              <input
                type="number"
                name="totalFeed"
                value={formData.totalFeed}
                onChange={handleInputChange}
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Feeding Frequency
              </label>
              <select
                name="feedingFrequency"
                value={formData.feedingFrequency}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="1x">1x per day</option>
                <option value="2x">2x per day</option>
                <option value="3x">3x per day</option>
                <option value="4x">4x per day</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fish Appetite Notes
              </label>
              <textarea
                name="appetiteNotes"
                value={formData.appetiteNotes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="How eagerly did the fish eat? Any observations..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors"
              >
                Save Feeding
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Feeding History */}
      <div className="space-y-4">
        {data.feedingLogs.length > 0 ? (
          [...data.feedingLogs].reverse().map((log) => (
            <motion.div
              key={log.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiCoffee} className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {format(new Date(log.date), 'MMM dd, yyyy')}
                    </h3>
                    <p className="text-sm text-gray-500">Feeding Record</p>
                  </div>
                </div>
                <button
                  onClick={() => deleteEntry('feedingLogs', log.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Feed</span>
                  <span className="text-lg font-semibold text-gray-900">{log.totalFeed}g</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Frequency</span>
                  <span className="text-sm font-medium text-gray-700">{log.feedingFrequency}</span>
                </div>
                {log.appetiteNotes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{log.appetiteNotes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiCoffee} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No feeding records yet</h3>
            <p className="text-gray-500 mb-6">Start tracking your fish feeding by adding your first record</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add First Record</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedingLog;